import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Alert, Radio } from '@dev/ee-components';
import styled from 'styled-components';

import { getUploadStore } from '@/store/upload/actions';
import { getAccountStore } from '@/store/account/actions';
import { AssignContactsProps } from '../../ContactsUpload';
import { getDecodedListName, ALL_CONTACTS_LIST } from '../..';
import { ContactSettings } from './ContactSettings';
import { ButtonFooterComponent } from './ButtonFooterComponent';

const NewListContainer = styled.div`
    & [role='radiogroup'] {
        flex-direction: row;
        flex-wrap: initial;
    }
    label {
        display: flex;
        justify-content: space-between;
        width: 50%;
        padding: 30px;
        border: 1px solid #e2e8f0;
        border-radius: 4px;
        margin-left: 0;
        margin-right: 16px;
        box-sizing: border-box;
    }
    label + label {
        margin-right: 0;
        margin-left: 16px;
    }
`;

const enableContactFeatures = (GeneralInfo): boolean => GeneralInfo?.EnableContactFeatures;

export const AssignContacts = (props: AssignContactsProps): JSX.Element => {
    const { t } = useTranslation();
    const { GeneralInfo } = useSelector(getAccountStore);
    const { segmentname, listname } = useParams<{ segmentname: string; listname: string }>();
    const isCreateListDisabled = !!getDecodedListName(listname) || !!segmentname;
    const upload = useSelector(getUploadStore);
    const {
        encoding,
        pattern,
        contactsPasted,
        setCurrentStep,
        sourceType,
        createList,
        setCreateList,
        setListName,
        listName,
        isValidListName,
        setValidListName,
        status,
        setStatus,
        consentTracking,
        setConsentTracking,
        overrideCustomFields,
    } = props;

    const radioOptions: Record<string, string | boolean>[] = [
        {
            id: 'existing-list',
            label: t('existinglist'),
            value: 'existing',
        },
    ];

    if (enableContactFeatures(GeneralInfo)) {
        radioOptions.push({
            id: 'new-list',
            label: t('newlist'),
            value: 'create',
            disabled: isCreateListDisabled,
        });
    }

    const uploadListname = upload?.fileuploaded?.FileName || 'New list';

    return (
        <>
            <NewListContainer>
                <Radio
                    id="choose-list-radios"
                    name="InputName"
                    value={createList ? 'create' : 'existing'}
                    radios={radioOptions}
                    onChange={(e): void => {
                        const value = (e.target as HTMLInputElement).value;
                        setCreateList(value === 'existing' ? false : true);
                        setListName(value === 'existing' ? ALL_CONTACTS_LIST : uploadListname);
                    }}
                    labelPlacement="start"
                />
            </NewListContainer>
            <div style={{ margin: '32px 0' }}>
                <Alert severity="info" body={t('cannotchangestatusofcontacts')} />
            </div>
            <ContactSettings
                createList={createList}
                listName={listName}
                setListName={setListName}
                isValidListName={isValidListName}
                setValidListName={setValidListName}
                status={status}
                setStatus={setStatus}
                setConsentTracking={setConsentTracking}
                consentTracking={consentTracking}
            />
            <ButtonFooterComponent
                setListName={setListName}
                sourceType={sourceType}
                encoding={encoding}
                pattern={pattern}
                contactsPasted={contactsPasted}
                setCurrentStep={setCurrentStep}
                createList={createList}
                listName={listName}
                setValidListName={setValidListName}
                status={status}
                consentTracking={consentTracking}
                overrideCustomFields={overrideCustomFields}
            />
        </>
    );
};